import { useState, useRef } from "react";
import { type Lang, type Translations } from "@/lib/i18n";

// CTA path-with-lang: /zh/ visitors clicking the gradient button arrive at
// the login page with ?lang=zh so detectLang picks up Chinese instead of
// falling back to navigator (many Chinese SEO visitors have non-zh-CN
// browser locale). This is the cross-route preference carrier; the /zh/
// path itself doesn't propagate past landing because /app/* has no zh mirror.
const appUrl = (lang: Lang): string =>
  lang === "zh" ? "/app/login?lang=zh" : "/app/login";

export const Hero = ({ scrollTo, tr, lang }: { scrollTo: (id: string) => void; tr: Translations; lang: Lang }) => {
  const headline = tr.hero.headline;
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(videoRef.current.muted);
    }
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center pt-20 pb-24 overflow-hidden bg-landing-hero-bg">
      <div className="max-w-7xl mx-auto px-6 md:px-12 w-full grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div className="z-10 animate-hero-slide-in">
          <h1 className="text-5xl md:text-7xl lg:text-8xl mb-8 leading-[1.05] font-landing-serif font-medium tracking-tight text-black italic whitespace-pre-line">
            {headline}
          </h1>
          <p className="text-lg md:text-xl text-black/60 mb-10 max-w-lg leading-relaxed font-landing-sans">
            {tr.hero.desc}
            <span className="block mt-4 font-semibold text-black">{tr.hero.subscription}</span>
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <a href={appUrl(lang)} className="btn-gradient px-10 py-4 label-vogue hover:opacity-90 transition-all shadow-xl shadow-landing-accent/20 cursor-pointer inline-block no-underline">
              {tr.nav.tryFree}
            </a>
            <button onClick={() => scrollTo("gallery")} className="border border-black/10 px-10 py-4 label-vogue hover:bg-black/5 transition-all cursor-pointer rounded-xl">
              {tr.hero.viewGallery}
            </button>
          </div>
        </div>
      </div>
      <div className="absolute right-0 top-0 w-full lg:w-1/2 h-full -z-10 lg:z-0">
        <div className="h-full w-full relative animate-hero-fade-in">
          <video ref={videoRef} autoPlay muted loop playsInline preload="none" poster="/landing/hero-poster.webp" className="w-full h-full object-cover">
            <source src="/landing/hero.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-r from-[#f5f2ed] via-[#f5f2ed]/30 lg:via-transparent to-transparent"></div>
          <button onClick={toggleMute} className="absolute bottom-6 right-6 z-10 w-10 h-10 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/50 transition-colors cursor-pointer" aria-label={isMuted ? tr.unmute : tr.mute}>
            {isMuted ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                <line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/>
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/>
              </svg>
            )}
          </button>
        </div>
      </div>
    </section>
  );
};
